import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Plus, 
  Trash2, 
  AlertCircle,
  Info
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TriggerConditionFormData,
  ConditionField,
  ConditionOperator,
  LogicOperator,
  TriggerType,
  CONDITION_FIELD_LABELS,
  OPERATOR_LABELS
} from '@/types/sms-triggers';

interface TriggerConditionBuilderProps {
  conditions: TriggerConditionFormData[];
  logic: LogicOperator;
  onChange: (conditions: TriggerConditionFormData[], logic: LogicOperator) => void;
  errors?: Record<string, string>;
  triggerType?: TriggerType;
}

// Field type mappings for validation
const FIELD_TYPES: Record<ConditionField, 'number' | 'string' | 'array' | 'date'> = {
  total_orders: 'number',
  total_spend: 'number',
  average_order_value: 'number',
  days_since_last_order: 'number',
  service_type: 'array',
  loyalty_tier: 'string'
};

// Operators allowed for each field type
const FIELD_OPERATORS: Record<string, ConditionOperator[]> = {
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'between'],
  string: ['equals', 'not_equals', 'contains', 'not_contains'],
  array: ['in', 'not_in'],
  date: ['equals', 'greater_than', 'less_than', 'between']
};

// Service type options
const SERVICE_TYPE_OPTIONS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'dine_in', label: 'Dine In' }
];

// Loyalty tier options
const LOYALTY_TIER_OPTIONS = [
  { value: 'bronze', label: 'Bronze' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'platinum', label: 'Platinum' }
];

const TriggerConditionBuilder: React.FC<TriggerConditionBuilderProps> = ({
  conditions,
  logic,
  onChange,
  errors = {},
  triggerType
}) => {
  const [localConditions, setLocalConditions] = useState<TriggerConditionFormData[]>(conditions);
  const [localLogic, setLocalLogic] = useState<LogicOperator>(logic);

  // Sync with parent when props change
  useEffect(() => {
    setLocalConditions(conditions);
    setLocalLogic(logic);
  }, [conditions, logic]);

  // Notify parent of changes
  const notifyChange = (newConditions: TriggerConditionFormData[], newLogic: LogicOperator) => {
    onChange(newConditions, newLogic);
  };

  const addCondition = () => {
    // For re_engagement triggers, don't allow additional days_since_last_order conditions
    const availableFields = Object.keys(CONDITION_FIELD_LABELS) as ConditionField[];
    const defaultField = triggerType === 're_engagement' 
      ? availableFields.find(f => f !== 'days_since_last_order') || 'total_orders'
      : 'total_orders';

    const newCondition: TriggerConditionFormData = {
      id: `condition_${Date.now()}`,
      field: defaultField,
      operator: 'equals',
      value: '',
      field_type: FIELD_TYPES[defaultField]
    };
    
    const newConditions = [...localConditions, newCondition];
    setLocalConditions(newConditions);
    notifyChange(newConditions, localLogic);
  };

  const removeCondition = (index: number) => {
    // Don't allow removing required conditions
    const condition = localConditions[index];
    if (condition.isRequired) {
      return;
    }
    
    const newConditions = localConditions.filter((_, i) => i !== index);
    setLocalConditions(newConditions);
    notifyChange(newConditions, localLogic);
  };

  const updateCondition = (index: number, updates: Partial<TriggerConditionFormData>) => {
    const newConditions = localConditions.map((condition, i) => {
      if (i === index) {
        const updatedCondition = { ...condition, ...updates };
        
        // If field changed, reset operator and value
        if (updates.field && updates.field !== condition.field) {
          const fieldType = FIELD_TYPES[updates.field];
          const allowedOperators = FIELD_OPERATORS[fieldType];
          
          updatedCondition.field_type = fieldType;
          updatedCondition.operator = allowedOperators[0];
          updatedCondition.value = fieldType === 'array' ? [] : '';
        }
        
        // If operator changed to/from 'between', reset value
        if (updates.operator && updates.operator !== condition.operator) {
          if (updates.operator === 'between' || condition.operator === 'between') {
            updatedCondition.value = updates.operator === 'between' ? { min: '', max: '' } : '';
          }
        }
        
        return updatedCondition;
      }
      return condition;
    });
    
    setLocalConditions(newConditions);
    notifyChange(newConditions, localLogic);
  };

  const updateLogic = (newLogic: LogicOperator) => {
    setLocalLogic(newLogic);
    notifyChange(localConditions, newLogic);
  };

  const renderValueInput = (condition: TriggerConditionFormData, index: number) => {
    const { field, operator, value, field_type } = condition;

    // Between operator - dual inputs
    if (operator === 'between') {
      return (
        <div className="flex items-center space-x-2">
          <Input
            type="number"
            placeholder="Min"
            value={value?.min || ''}
            onChange={(e) => updateCondition(index, { 
              value: { ...value, min: e.target.value } 
            })}
            className="w-24"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <Input
            type="number"
            placeholder="Max"
            value={value?.max || ''}
            onChange={(e) => updateCondition(index, { 
              value: { ...value, max: e.target.value } 
            })}
            className="w-24"
          />
        </div>
      );
    }

    // Array operators - multi-select
    if (operator === 'in' || operator === 'not_in') {
      const selectedValues = Array.isArray(value) ? value : [];
      
      if (field === 'service_type') {
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {SERVICE_TYPE_OPTIONS.map(option => (
                <Badge
                  key={option.value}
                  variant={selectedValues.includes(option.value) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    const newValues = selectedValues.includes(option.value)
                      ? selectedValues.filter(v => v !== option.value)
                      : [...selectedValues, option.value];
                    updateCondition(index, { value: newValues });
                  }}
                >
                  {option.label}
                </Badge>
              ))}
            </div>
            {selectedValues.length === 0 && (
              <p className="text-xs text-muted-foreground">Select at least one service type</p>
            )}
          </div>
        );
      }

      if (field === 'loyalty_tier') {
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {LOYALTY_TIER_OPTIONS.map(option => (
                <Badge
                  key={option.value}
                  variant={selectedValues.includes(option.value) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    const newValues = selectedValues.includes(option.value)
                      ? selectedValues.filter(v => v !== option.value)
                      : [...selectedValues, option.value];
                    updateCondition(index, { value: newValues });
                  }}
                >
                  {option.label}
                </Badge>
              ))}
            </div>
            {selectedValues.length === 0 && (
              <p className="text-xs text-muted-foreground">Select at least one loyalty tier</p>
            )}
          </div>
        );
      }
    }

    // Single select for string fields
    if (field_type === 'string' && (operator === 'equals' || operator === 'not_equals')) {
      if (field === 'loyalty_tier') {
        return (
          <Select
            value={value || ''}
            onValueChange={(newValue) => updateCondition(index, { value: newValue })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Select tier" />
            </SelectTrigger>
            <SelectContent>
              {LOYALTY_TIER_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
    }

    // Default input based on field type
    const inputType = field_type === 'number' ? 'number' : 'text';
    const placeholder = field_type === 'number' ? '0' : 'Enter value';

    return (
      <Input
        type={inputType}
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => updateCondition(index, { value: e.target.value })}
        className="w-32"
      />
    );
  };

  const getFieldDescription = (field: ConditionField) => {
    const descriptions: Record<ConditionField, string> = {
      total_orders: 'Total number of orders placed by the customer',
      total_spend: 'Total amount spent by the customer (in dollars)',
      average_order_value: 'Average order value for the customer (in dollars)',
      days_since_last_order: 'Number of days since customer\'s last order',
      service_type: 'Type of service used (delivery, pickup, dine-in)',
      loyalty_tier: 'Customer\'s loyalty program tier'
    };
    return descriptions[field] || '';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Trigger Conditions</h3>
          <p className="text-sm text-muted-foreground">
            Define when this trigger should activate based on customer data
          </p>
        </div>
        <Button onClick={addCondition} size="sm" className="gap-1">
          <Plus className="h-4 w-4" />
          Add Condition
        </Button>
      </div>

      {localConditions.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No conditions defined</h3>
            <p className="text-muted-foreground mb-4">
              Add at least one condition to specify when this trigger should activate
            </p>
            <Button onClick={addCondition}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Condition
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Logic operator selector for multiple conditions */}
          {localConditions.length > 1 && (
            <div className="flex items-center space-x-4">
              <Label>Conditions must match:</Label>
              <Select 
                value={localLogic} 
                onValueChange={updateLogic}
                disabled={triggerType === 're_engagement'}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">All (AND)</SelectItem>
                  <SelectItem value="OR">Any (OR)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-sm text-muted-foreground">
                {triggerType === 're_engagement' 
                  ? 'All conditions must be met for re-engagement triggers'
                  : (localLogic === 'AND' 
                    ? 'Customer must meet all conditions'
                    : 'Customer must meet at least one condition'
                  )
                }
              </div>
            </div>
          )}

          {/* Conditions list */}
          <div className="space-y-4">
            {localConditions.map((condition, index) => (
              <div key={condition.id}>
                {index > 0 && (
                  <div className="flex items-center my-2">
                    <Separator className="flex-1" />
                    <Badge variant="outline" className="mx-4">
                      {localLogic}
                    </Badge>
                    <Separator className="flex-1" />
                  </div>
                )}
                
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start space-x-4">
                        {/* Field selector */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 h-5">
                            <Label>Field</Label>
                            {condition.isRequired && (
                              <Badge variant="secondary" className="text-xs">
                                Required
                              </Badge>
                            )}
                          </div>
                          <Select
                            value={condition.field}
                            onValueChange={(value) => updateCondition(index, { field: value as ConditionField })}
                            disabled={condition.isRequired}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(CONDITION_FIELD_LABELS)
                                .filter(([value]) => {
                                  // For re_engagement triggers, don't show days_since_last_order as an option
                                  // for additional conditions (it's already required)
                                  if (triggerType === 're_engagement' && value === 'days_since_last_order' && !condition.isRequired) {
                                    return false;
                                  }
                                  return true;
                                })
                                .map(([value, label]) => (
                                  <SelectItem key={value} value={value}>
                                    {label}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Operator selector */}
                        <div className="w-40">
                          <div className="h-5 mb-2">
                            <Label>Operator</Label>
                          </div>
                          <Select
                            value={condition.operator}
                            onValueChange={(value) => updateCondition(index, { operator: value as ConditionOperator })}
                            disabled={condition.isRequired}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_OPERATORS[condition.field_type].map(operator => (
                                <SelectItem key={operator} value={operator}>
                                  {OPERATOR_LABELS[operator]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Value input */}
                        <div className="flex-1">
                          <div className="h-5 mb-2">
                            <Label>Value</Label>
                          </div>
                          {renderValueInput(condition, index)}
                        </div>

                        {/* Remove button */}
                        <div className="pt-6">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCondition(index)}
                            disabled={localConditions.length === 1 || condition.isRequired}
                            title={condition.isRequired ? 'This condition is required' : 'Remove condition'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Field description - moved below the inputs */}
                      <p className="text-xs text-muted-foreground pl-1">
                        {getFieldDescription(condition.field)}
                      </p>
                    </div>

                    {/* Condition error */}
                    {errors[`condition_${index}`] && (
                      <Alert variant="destructive" className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          {errors[`condition_${index}`]}
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Full-width Add Condition button at bottom */}
          <Button 
            variant="outline" 
            className="w-full gap-2 mt-4" 
            onClick={addCondition}
          >
            <Plus className="h-4 w-4" />
            Add Condition
          </Button>
        </div>
      )}

      {/* Condition Summary */}
      {localConditions.length > 0 && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <Label className="text-sm font-medium mb-2 block">Condition Summary:</Label>
          <p className="text-sm text-muted-foreground">
            This trigger will activate when{' '}
            <span className="font-medium">
              {localLogic === 'AND' ? 'ALL' : 'ANY'}
            </span>{' '}
            of the following conditions are met:
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {localConditions.map((condition, index) => (
              <li key={condition.id} className="flex items-center gap-2">
                <span className="w-4 h-4 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-medium">
                  {index + 1}
                </span>
                <span>
                  {CONDITION_FIELD_LABELS[condition.field]} {OPERATOR_LABELS[condition.operator].toLowerCase()}{' '}
                  {Array.isArray(condition.value) 
                    ? condition.value.join(', ')
                    : typeof condition.value === 'object' && condition.value !== null && 'min' in condition.value && 'max' in condition.value
                    ? `${condition.value.min || 'min'} to ${condition.value.max || 'max'}`
                    : condition.value
                  }
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TriggerConditionBuilder;